import React, { useState, useEffect } from 'react';
import { 
  Video, TrendingUp, Users, BarChart3, Settings, 
  Youtube, Shield, Eye, Plus, PlayCircle, Star,
  Server, Brain, Target, Clock, Database, Globe, AlertCircle,
  Sparkles, Award, Heart, BookOpen, Lightbulb, Zap,
  Activity, Coffee, Crown, CheckCircle, Search, FileText
} from 'lucide-react';

import AdminNavigation from './AdminNavigation';
import AdminVideoDiscovery from './AdminVideoDiscovery';
import AdminVideoManager from './AdminVideoManager';
import AladinnAcademy from '../CP-Academy/AladinnAcademy';
import videoServices from '../CP-Academy/VideoService';
import sourceService from '../CP-Sourcing/SourceService';

// Mock Academy component for demo
const MockAladinnAcademy = ({ isAdminMode = false }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Aladinn Academy {isAdminMode && '- Mode Administrateur'}
              </h1>
              <p className="text-gray-600">
                {isAdminMode 
                  ? 'Interface Academy avec outils d\'administration intégrés'
                  : 'Plateforme de formation et développement professionnel'
                }
              </p>
            </div>

            {isAdminMode && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="w-6 h-6 text-blue-600" />
                  <h3 className="text-lg font-semibold text-blue-900">Outils d'Administration</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <button className="bg-white p-4 rounded-lg border border-blue-200 hover:bg-blue-50 transition-colors">
                    <Video className="w-5 h-5 text-blue-600 mb-2" />
                    <div className="text-sm font-medium text-blue-900">Gestion Contenu</div>
                  </button>
                  <button className="bg-white p-4 rounded-lg border border-blue-200 hover:bg-blue-50 transition-colors">
                    <Users className="w-5 h-5 text-blue-600 mb-2" />
                    <div className="text-sm font-medium text-blue-900">Utilisateurs</div>
                  </button>
                  <button className="bg-white p-4 rounded-lg border border-blue-200 hover:bg-blue-50 transition-colors">
                    <BarChart3 className="w-5 h-5 text-blue-600 mb-2" />
                    <div className="text-sm font-medium text-blue-900">Analytics</div>
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {['Leadership', 'Technologie', 'Entrepreneuriat', 'Finance', 'Créativité', 'Santé'].map((theme, index) => (
                <div key={theme} className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-all">
                  <div className={`w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center mb-4`}>
                    <BookOpen className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{theme}</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Formations spécialisées en {theme.toLowerCase()}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{Math.floor(Math.random() * 20) + 5} formations</span>
                    <button className="text-blue-600 hover:text-blue-700">
                      <PlayCircle className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [dashboardStats, setDashboardStats] = useState({
    totalVideos: 0,
    backendSources: 0,
    recentlyAdded: 0,
    awaitingCuration: 0,
    totalViews: 0,
    activeThemes: 6,
    topPerformers: [],
    byTheme: {
      leadership: 0,
      technology: 0,
      entrepreneurship: 0,
      finance: 0,
      creativity: 0,
      health: 0
    },
    byDiscoveryMethod: {
      backend: 0,
      manual: 0,
      youtube: 0
    }
  });

  // Charger les vraies statistiques
  useEffect(() => {
    loadRealStats();
  }, []);

  const loadRealStats = async () => {
    try {
      // Récupérer toutes les vidéos
      const allVideos = await videoServices.getAllVideos();
      
      // Calculer les statistiques
      const totalVideos = allVideos.length;
      const backendSources = allVideos.filter(v => v.discoveryMethod === 'backend_ai' || v.discoveryMethod === 'backend').length;
      const recentlyAdded = allVideos.filter(v => {
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return new Date(v.addedAt) > weekAgo;
      }).length;
      
      const totalViews = allVideos.reduce((sum, v) => sum + (v.viewCount || 0), 0);
      
      // Par thème
      const byTheme = {};
      allVideos.forEach(video => {
        byTheme[video.theme] = (byTheme[video.theme] || 0) + 1;
      });
      
      // Par méthode de découverte
      const byDiscoveryMethod = {
        backend: allVideos.filter(v => v.discoveryMethod === 'backend_ai' || v.discoveryMethod === 'backend').length,
        manual: allVideos.filter(v => v.discoveryMethod === 'manual').length,
        youtube: allVideos.filter(v => v.discoveryMethod === 'youtube').length
      };
      
      // Top performers (vidéos les plus vues)
      const topPerformers = allVideos
        .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))
        .slice(0, 3)
        .map(v => ({
          id: v.id,
          title: v.title,
          views: v.viewCount || 0,
          channel: v.domain || 'Source inconnue'
        }));

      setDashboardStats({
        totalVideos,
        backendSources,
        recentlyAdded,
        awaitingCuration: 0, // À calculer selon vos besoins
        totalViews,
        activeThemes: Object.keys(byTheme).length,
        topPerformers,
        byTheme,
        byDiscoveryMethod
      });
      
    } catch (error) {
      console.error('Erreur chargement statistiques:', error);
    }
  };

  const DashboardOverview = () => (
    <div className="p-8">
      {/* Header avec gradient */}
      <div className="mb-8">
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 text-white rounded-2xl p-8 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">✨ Tableau de bord Administrateur</h1>
              <p className="text-blue-100">Gestion intelligente avec IA • Curation automatisée • Alignement valeurs Aladinn</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-xl">
                <div className="flex items-center gap-2">
                  <Brain className="w-5 h-5" />
                  <span className="font-medium">IA Active</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="bg-blue-100 p-3 rounded-xl">
              <Video className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <div className="text-3xl font-bold text-gray-900">{dashboardStats.totalVideos}</div>
              <div className="text-sm text-gray-600">Total formations</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="bg-purple-100 p-3 rounded-xl">
              <Sparkles className="w-8 h-8 text-purple-600" />
            </div>
            <div>
              <div className="text-3xl font-bold text-gray-900">{dashboardStats.backendSources}</div>
              <div className="text-sm text-gray-600">Via IA Backend</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="bg-green-100 p-3 rounded-xl">
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <div className="text-3xl font-bold text-gray-900">{dashboardStats.recentlyAdded}</div>
              <div className="text-sm text-gray-600">Cette semaine</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="bg-orange-100 p-3 rounded-xl">
              <Target className="w-8 h-8 text-orange-600" />
            </div>
            <div>
              <div className="text-3xl font-bold text-gray-900">{dashboardStats.awaitingCuration}</div>
              <div className="text-sm text-gray-600">À curater</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="bg-red-100 p-3 rounded-xl">
              <Eye className="w-8 h-8 text-red-600" />
            </div>
            <div>
              <div className="text-3xl font-bold text-gray-900">
                {dashboardStats.totalViews > 1000 ? `${(dashboardStats.totalViews / 1000).toFixed(1)}k` : dashboardStats.totalViews}
              </div>
              <div className="text-sm text-gray-600">Vues totales</div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* AI-Powered Workflow */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-2xl border border-blue-200 shadow-sm">
          <h3 className="text-xl font-bold text-blue-900 mb-6 flex items-center gap-3">
            <Brain className="w-6 h-6" />
            🚀 Workflow IA de Découverte
          </h3>
          <div className="space-y-4">
            <button
              onClick={() => setCurrentPage('discovery')}
              className="w-full text-left p-6 rounded-xl bg-white border border-blue-200 hover:bg-blue-50 transition-all group shadow-sm"
            >
              <div className="flex items-center gap-4">
                <div className="bg-purple-100 group-hover:bg-purple-200 p-4 rounded-xl transition-colors">
                  <Search className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">1. Découverte Intelligente</div>
                  <div className="text-sm text-gray-600">IA analyse VideoEducation, DuckDuckGo, Google</div>
                  <div className="text-xs text-purple-600 mt-1">• Scoring automatique • Alignement valeurs</div>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => setCurrentPage('discovery')}
              className="w-full text-left p-6 rounded-xl bg-white border border-green-200 hover:bg-green-50 transition-all group shadow-sm"
            >
              <div className="flex items-center gap-4">
                <div className="bg-green-100 group-hover:bg-green-200 p-4 rounded-xl transition-colors">
                  <Target className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">2. Curation Assistée</div>
                  <div className="text-sm text-gray-600">Recommandations IA + validation humaine</div>
                  <div className="text-xs text-green-600 mt-1">• Critères Aladinn • Analyse éthique</div>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => setCurrentPage('academy-public')}
              className="w-full text-left p-6 rounded-xl bg-white border border-indigo-200 hover:bg-indigo-50 transition-all group shadow-sm"
            >
              <div className="flex items-center gap-4">
                <div className="bg-indigo-100 group-hover:bg-indigo-200 p-4 rounded-xl transition-colors">
                  <CheckCircle className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">3. Publication Automatique</div>
                  <div className="text-sm text-gray-600">Contenu validé → Academy en direct</div>
                  <div className="text-xs text-indigo-600 mt-1">• Qualité garantie • Impact mesuré</div>
                </div>
              </div>
            </button>
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <Zap className="w-6 h-6" />
            Actions Rapides
          </h3>
          <div className="space-y-4">
            <button
              onClick={() => setCurrentPage('discovery')}
              className="w-full text-left p-6 rounded-xl border border-gray-200 hover:bg-blue-50 hover:border-blue-200 transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="bg-blue-100 group-hover:bg-blue-200 p-4 rounded-xl transition-colors">
                  <Search className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Découverte IA Vidéo</div>
                  <div className="text-sm text-gray-600">Rechercher de nouvelles formations</div>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => setCurrentPage('videos')}
              className="w-full text-left p-6 rounded-xl border border-gray-200 hover:bg-purple-50 hover:border-purple-200 transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="bg-purple-100 group-hover:bg-purple-200 p-4 rounded-xl transition-colors">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Gestion des Vidéos</div>
                  <div className="text-sm text-gray-600">Administrer la bibliothèque de formations</div>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => setCurrentPage('academy-admin')}
              className="w-full text-left p-6 rounded-xl border border-gray-200 hover:bg-green-50 hover:border-green-200 transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="bg-green-100 group-hover:bg-green-200 p-4 rounded-xl transition-colors">
                  <Shield className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Academy Admin</div>
                  <div className="text-sm text-gray-600">Interface avec outils administrateur</div>
                </div>
              </div>
            </button>
            
            <a
              href="/academy"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-left p-6 rounded-xl border border-gray-200 hover:bg-orange-50 hover:border-orange-200 transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="bg-orange-100 group-hover:bg-orange-200 p-4 rounded-xl transition-colors">
                  <Globe className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Site Public</div>
                  <div className="text-sm text-gray-600">Aperçu utilisateur final</div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* AI-Enhanced Discovery Methods */}
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <BarChart3 className="w-6 h-6" />
            Méthodes de Découverte IA
          </h3>
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-purple-500 rounded-full"></div>
                <span className="text-sm font-medium text-gray-700">Backend IA</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold text-gray-900">{dashboardStats.byDiscoveryMethod.backend}</span>
                <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                  {dashboardStats.totalVideos > 0 ? Math.round((dashboardStats.byDiscoveryMethod.backend / dashboardStats.totalVideos) * 100) : 0}%
                </span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                <span className="text-sm font-medium text-gray-700">Curation Manuelle</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold text-gray-900">{dashboardStats.byDiscoveryMethod.manual}</span>
                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                  {dashboardStats.totalVideos > 0 ? Math.round((dashboardStats.byDiscoveryMethod.manual / dashboardStats.totalVideos) * 100) : 0}%
                </span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                <span className="text-sm font-medium text-gray-700">YouTube Direct</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold text-gray-900">{dashboardStats.byDiscoveryMethod.youtube}</span>
                <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                  {dashboardStats.totalVideos > 0 ? Math.round((dashboardStats.byDiscoveryMethod.youtube / dashboardStats.totalVideos) * 100) : 0}%
                </span>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200">
              <div className="text-sm text-purple-800">
                <strong>
                  {dashboardStats.totalVideos > 0 ? Math.round((dashboardStats.byDiscoveryMethod.backend / dashboardStats.totalVideos) * 100) : 0}%
                </strong> du contenu découvert par l'IA
              </div>
              <div className="text-xs text-purple-600 mt-1">
                ↗️ Évolution positive • Qualité garantie
              </div>
            </div>
          </div>
        </div>
        
        {/* Top Performers with AI insights */}
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <Star className="w-6 h-6" />
            Top Performances
          </h3>
          <div className="space-y-4">
            {dashboardStats.topPerformers.length > 0 ? (
              dashboardStats.topPerformers.map((video, index) => (
                <div key={video.id} className="flex items-center gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors border border-gray-100">
                  <div className="text-lg font-bold text-purple-600 w-8 text-center">
                    #{index + 1}
                  </div>
                  <div className="w-12 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded flex items-center justify-center">
                    <PlayCircle className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 line-clamp-1">{video.title}</div>
                    <div className="text-sm text-gray-500 flex items-center gap-4">
                      <span>{video.views > 1000 ? `${(video.views / 1000).toFixed(1)}k` : video.views} vues</span>
                      <span>{video.channel}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Video className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Aucune donnée de performance disponible</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const MainContent = () => {
    switch (currentPage) {
      case 'dashboard':
        return <DashboardOverview />;
      case 'discovery':
        return <AdminVideoDiscovery />;
      case 'videos':
        return <AdminVideoManager />;
      case 'academy-admin':
        return <MockAladinnAcademy isAdminMode={true} />;
      case 'academy-public':
        return <MockAladinnAcademy isAdminMode={false} />;
      default:
        return <DashboardOverview />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <AdminNavigation 
        currentPage={currentPage}
        onNavigate={setCurrentPage}
      />
      
      {/* Main content - Fixed: proper margin to avoid overlap */}
      <div className="ml-72">
        <main className="min-h-screen">
          <MainContent />
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;